import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { z } from "zod";

import { useDashboardRealtime } from "../../../../hooks/useDashboardRealtime";
import { type BlackboardInstance } from "../../blackboardModels";
import { DiffOverlay } from "../../observability/DiffOverlay";
import {
  cacheHitPct,
  flagsFor,
  type SnippetEntity,
  type StepDetail,
  type StepEntity,
  type WakeSummary,
} from "../../observability/observabilityModel";
import { SnippetOverlay } from "../../observability/Overlays";
import { StepDetailOverlay } from "../../observability/StepDetailOverlay";
import { WakeRow } from "../../observability/WakeRow";
import { PillButton, RetroEmpty, RetroError } from "../../retro";

const wakeListResponseSchema = z.custom<{ wakes: WakeSummary[] }>();
const wakeStepsResponseSchema = z.custom<{ steps: StepEntity[] }>();
const stepDetailResponseSchema = z.custom<StepDetail>();
const snippetResponseSchema = z.custom<SnippetEntity>();
const diffResponseSchema = z.custom<{ a: StepDetail; b: StepDetail }>();

// ---------------------------------------------------------------------------
// Window selector — matches CostTab vocabulary exactly.
// ---------------------------------------------------------------------------

type WindowKey = "1h" | "24h" | "7d";
const WINDOWS: { label: string; value: WindowKey; hours: number }[] = [
  { hours: 1, label: "1h", value: "1h" },
  { hours: 24, label: "24h", value: "24h" },
  { hours: 24 * 7, label: "7d", value: "7d" },
];

interface ObservabilityTabProps {
  instance: BlackboardInstance;
}

export const ObservabilityTab: FC<ObservabilityTabProps> = ({ instance }) => {
  const [windowKey, setWindowKey] = useState<WindowKey>("24h");
  const [wakes, setWakes] = useState<WakeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expandedWakeId, setExpandedWakeId] = useState<string | null>(null);
  const [wakeSteps, setWakeSteps] = useState<Record<string, StepEntity[]>>({});
  const [openStepDetail, setOpenStepDetail] = useState<StepDetail | null>(null);
  const [openSnippet, setOpenSnippet] = useState<SnippetEntity | null>(null);
  const [diffResult, setDiffResult] = useState<{
    a: StepDetail;
    b: StepDetail;
  } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const hours = WINDOWS.find((w) => w.value === windowKey)?.hours ?? 24;
      const resp = await fetch(
        `/user/instances/${instance.id}/observability/list?window_hours=${hours}&limit=50`,
        { credentials: "include" },
      );
      if (resp.status === 403) {
        throw new Error(
          "Observability requires platform admin. Ask a platform admin to grant access.",
        );
      }
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const json = wakeListResponseSchema.parse(await resp.json());
      setWakes(json.wakes);
    } catch (error) {
      setErr(
        error instanceof Error ? error.message : "Failed to load observability",
      );
    } finally {
      setLoading(false);
    }
  }, [instance.id, windowKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const { subscribe } = useDashboardRealtime();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wakesWithFlags = useMemo(() => {
    // Compute flags against the prior wake in the series (oldest-first pass,
    // Then restore newest-first for display). Same logic as server-side so
    // Admin UI stays consistent with MCP tools.
    const oldestFirst = [...wakes].toReversed();
    const flagged = oldestFirst.map((w, i) => ({
      anomalies: flagsFor(w, i > 0 ? oldestFirst[i - 1] : undefined),
      hitPct: cacheHitPct(w),
      wake: w,
    }));
    return flagged.toReversed();
  }, [wakes]);

  const loadWakeSteps = useCallback(
    async (wakeGroupId: string): Promise<StepEntity[] | null> => {
      const resp = await fetch(
        `/user/instances/${instance.id}/observability/wake/${wakeGroupId}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        return null;
      }
      const json = wakeStepsResponseSchema.parse(await resp.json());
      setWakeSteps((prev) => ({ ...prev, [wakeGroupId]: json.steps }));
      return json.steps;
    },
    [instance.id],
  );

  // Realtime push — when the node broadcasts `step_completed` for this
  // Instance, refetch the wake list (and the expanded wake's steps when the
  // Event belongs to it). The event is a signal, not a payload — authoritative
  // Shape still comes from REST so server-side aggregation stays correct.
  //
  // 150ms debounce coalesces bursts of events from multi-step wakes into one
  // Refetch; without this a tool-iterating wake would trigger N list fetches.
  useEffect(() => {
    const unsubscribe = subscribe(
      (message) => {
        if (message.type !== "instance_activity") {
          return;
        }
        if (message.payload.instance_id !== instance.id) {
          return;
        }
        if (message.payload.activity_type !== "step_completed") {
          return;
        }

        const { detail } = message.payload;
        const wakeGroupId =
          typeof detail.wake_group_id === "string"
            ? detail.wake_group_id
            : null;

        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = null;
          void refresh();
          if (wakeGroupId && expandedWakeId === wakeGroupId) {
            void loadWakeSteps(wakeGroupId);
          }
        }, 150);
      },
      { topic: "internalEventTopic" },
    );
    return () => {
      unsubscribe();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [subscribe, instance.id, refresh, expandedWakeId, loadWakeSteps]);

  const toggleWake = useCallback(
    (wakeGroupId: string) => {
      if (expandedWakeId === wakeGroupId) {
        setExpandedWakeId(null);
      } else {
        setExpandedWakeId(wakeGroupId);
        if (!wakeSteps[wakeGroupId]) {
          void loadWakeSteps(wakeGroupId);
        }
      }
    },
    [expandedWakeId, loadWakeSteps, wakeSteps],
  );

  const openStep = useCallback(
    async (stepId: string) => {
      const resp = await fetch(
        `/user/instances/${instance.id}/observability/step/${stepId}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        return;
      }
      const detail = stepDetailResponseSchema.parse(await resp.json());
      setOpenStepDetail(detail);
    },
    [instance.id],
  );

  const loadSnippetSha = useCallback(
    async (sha: string) => {
      const resp = await fetch(
        `/user/instances/${instance.id}/observability/snippet/${sha}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        return null;
      }
      return snippetResponseSchema.parse(await resp.json());
    },
    [instance.id],
  );

  const openSnippetSha = useCallback(
    async (sha: string) => {
      const snippet = await loadSnippetSha(sha);
      if (snippet) {
        setOpenSnippet(snippet);
      }
    },
    [loadSnippetSha],
  );

  // Diff: the user clicks "diff vs prior" on a step with step_index>0 OR on a
  // Wake row. We fetch both step details and show the diff view.
  const openDiff = useCallback(
    async (stepAId: string, stepBId: string) => {
      const resp = await fetch(
        `/user/instances/${instance.id}/observability/diff?a=${stepAId}&b=${stepBId}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        return;
      }
      const json = diffResponseSchema.parse(await resp.json());
      setDiffResult(json);
    },
    [instance.id],
  );

  return (
    <div className="relative flex h-full flex-col overflow-x-hidden">
      {/* Window selector — matches CostTab vocabulary. */}
      <div className="flex items-center gap-1.5 border-b border-emerald-500/20 bg-slate-100/60 px-4 py-2 dark:border-emerald-400/10 dark:bg-slate-950/40">
        {WINDOWS.map((opt) => (
          <PillButton
            key={opt.value}
            active={opt.value === windowKey}
            onClick={() => setWindowKey(opt.value)}
          >
            {opt.label}
          </PillButton>
        ))}
        <span className="ml-2 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500">
          · {wakes.length} wakes
        </span>
        {loading && (
          <span className="ml-auto font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/60">
            [ SYNCING… ]
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {err && (
          <div className="px-4 py-2">
            <RetroError message={err} />
          </div>
        )}
        {!loading && wakesWithFlags.length === 0 && !err && (
          <RetroEmpty
            label="NO WAKES"
            sub="no LLM calls captured in the selected window"
          />
        )}
        {wakesWithFlags.map(({ wake, anomalies, hitPct }, i) => {
          const priorWake = wakesWithFlags[i + 1]?.wake;
          const priorHitPct = wakesWithFlags[i + 1]?.hitPct;
          return (
            <WakeRow
              key={wake.wakeGroupId}
              wake={wake}
              anomalies={anomalies}
              hitPct={hitPct}
              priorHitPct={priorHitPct}
              expanded={expandedWakeId === wake.wakeGroupId}
              onToggle={() => toggleWake(wake.wakeGroupId)}
              steps={wakeSteps[wake.wakeGroupId]}
              onOpenStep={openStep}
              onDiffPrior={
                priorWake
                  ? async () => {
                      // Capture the fetched step ids directly — reading from
                      // `wakeSteps` state after `await loadWakeSteps()` sees
                      // The stale closure value because React batches the
                      // SetWakeSteps update; the returned array is authoritative.
                      const [currentSteps, priorSteps] = await Promise.all([
                        wakeSteps[wake.wakeGroupId]
                          ? Promise.resolve(wakeSteps[wake.wakeGroupId])
                          : loadWakeSteps(wake.wakeGroupId),
                        wakeSteps[priorWake.wakeGroupId]
                          ? Promise.resolve(wakeSteps[priorWake.wakeGroupId])
                          : loadWakeSteps(priorWake.wakeGroupId),
                      ]);
                      const a = priorSteps?.[0];
                      const b = currentSteps?.[0];
                      if (a && b) {
                        await openDiff(a.id, b.id);
                      }
                    }
                  : undefined
              }
            />
          );
        })}
      </div>

      {openStepDetail && (
        <StepDetailOverlay
          detail={openStepDetail}
          onClose={() => setOpenStepDetail(null)}
          onLoadSnippet={loadSnippetSha}
          onOpenSnippet={openSnippetSha}
        />
      )}
      {openSnippet && (
        <SnippetOverlay
          snippet={openSnippet}
          onClose={() => setOpenSnippet(null)}
        />
      )}
      {diffResult && (
        <DiffOverlay
          a={diffResult.a}
          b={diffResult.b}
          onClose={() => setDiffResult(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Duration cell with lock-wait / turn split.
// Stacked two-line cell: top = total wall-clock, bottom = "L:Xs" lock wait.
// The bottom line only renders when lockWaitMs ≥ LOCK_WAIT_VISIBLE_MS so
// Uncontended wakes stay single-line. Amber tint for the lock portion ties
// It to the "waiting" / "contended" color family used in the lock badges
// Elsewhere on the dashboard.
// ---------------------------------------------------------------------------

export { AnomalyBadge, ErrorKindBadge } from "../../observability/badges";
export {
  CompositionContextHeader,
  ReplayDiffBanner,
  ReplayDiffPrefixRows,
  ReplayPrefixBand,
} from "../../observability/contextHeader";
export {
  BlockStack,
  GenerationStatsReady,
  GenerationStatsSection,
  MetaCell,
  ProviderErrorBodySection,
  ReasoningBlockDisclosure,
  StepDetailOverlay,
  StepMetaGrid,
  initialGenerationStatsState,
} from "../../observability/StepDetailOverlay";
export { DiffOverlay } from "../../observability/DiffOverlay";
export { Overlay, SnippetOverlay } from "../../observability/Overlays";
export { StepList, StepOutputCell } from "../../observability/StepList";
export {
  STEP_KIND_COLORS,
  STEP_KIND_LABELS,
  WakeDurationCell,
  WakeRow,
} from "../../observability/WakeRow";
export {
  cacheHitPct,
  flagsFor,
  fmtDuration,
  fmtHitPct,
  fmtTime,
  fmtTokens,
  formatCacheMarkerValid,
  formatStepErrorLabel,
  formatWakeReplayBadge,
  formatWakeReplayTitle,
  getMetaAccentClass,
  getReplayContextSummary,
  hitPctColor,
  hitPctTooltip,
  modelRefTail,
  shortSha,
  totalWallClockMs,
  type AnomalyFlag,
  type ContentBlockEntity,
  type GenerationStatsResponse,
  type GenerationStatsState,
  type MessageEntity,
  type ProviderErrorFields,
  type ReplayContextFields,
  type ReplayContextSummary,
  type SnippetEntity,
  type SourceKind,
  type StepDetail,
  type StepEntity,
  type StepKind,
  type WakeSummary,
} from "../../observability/observabilityModel";
